import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { ReactComponent as CoinIcon } from '../assets/ico-coin.svg';
import { ReactComponent as DiscountIcon } from '../assets/ico-discount.svg';
import { ReactComponent as InqueryPointer } from '../assets/inquiry-pointer.svg';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import ChargeModal from '../components/modal/charge';
import InquiryModal from '../components/modal/inquiry';
import useAxios from '../hooks/useAxios';
import useIsElectron from '../hooks/useIsElectron';
import useIsMobile from '../hooks/useIsMobile';
import { modalState } from '../states/modalState';
import {
  alertMessage,
  formatDateString,
  formatPriceComma,
  getOrigin,
  isPC,
} from '../utils';

interface PaymentHistory {
  approved_at: string;
  order_id: string;
  product_name: string;
  total_amount: number;
}

const COINS_FOR_ANALYZE = 4;
const COINS_FOR_CHAT = 1;
const CHARGE_UNIT = 10;
const MAX_CHARGABLE_COINS = 200;

interface PaymentReadyResponse {
  next_redirect_app_url: string;
  next_redirect_mobile_url: string;
  next_redirect_pc_url: string;
}

enum ChargeType {
  COIN = 'hemochat_coin',
  BASIC = 'hemochat_basic',
  PRO = 'hemochat_pro',
}

// 우선 연결해주셔야할 것은 /payment/ready/와 /payment/approve/ 두개의 api입니다.

// 1. /payment/ready/
// body에 담을 정보
// 1) product_id: hemochat_coin,hemochat_basic,hemochat_pro 중 하나입니다.
// 2) quantity: 주문 수량입니다. hemochat_coin의 경우 10 이상부터 10의 배수 단위로 결제 가능하게 하도록 구성해주시면 될 것 같습니다.(주문창에 디폴트로 10 넣어놓고, +버튼 누르면 10씩 증가하는 방식)

// response에 담기는 정보
// 1) next_redirect_app_url: 앱에서 쓰는 것이므로 무시
// 2) next_redirect_mobile_url: 모바일 웹 환경에서는 이 주소를 활용해야합니다.
// 3) next_redirect_pc_url: pc웹 환경에서는 이 주소를 활용해야합니다.
// 리스폰스로 오는 위 주소 중 하나를 클라이언트 환경에 맞게 추출하신뒤, 그 주소로 리다이렉트 해주세요.
// 해당 주소로 이동하면, 카카오 로그인 및 결제창이 뜨고, 유저가 해당과정을 완료하면 카카오측에서 재차 리다이렉트 주소를 제공해줍니다.

// 2.  /payment/approve/
// 위에서 언급했듯이 유저가 카카오앱으로 로그인,결제 등을 완료하면, 자동으로 카카오측에서 리다이렉트 주소를 담아 클라이언트에 다시 응답을 보내줍니다.
// 이 주소는 "/payment/approve/ + 결제에 필요한 여러 식별정보를 경로파라미터에 담은 값"인데, 프론트에서는 별도 조작할 필요없이 그냥 카카오에서 주는 url 그대로 받아서 리다이렉트 시키시면 됩니다.
// 백엔드에 이 주소를 기다리는 api가 준비되어있고, 여기서 결제의 위조여부 등을 검증한뒤 최종적으로 코인을 지급합니다.

// response는 200 ok 아니면 400으로 처리됩니다. 400은 실패를 의미하는데, 일단 성공시나리오까지만 구현하시고 알려주세요.

const isMultipleOfTen = (num: number) => num % 10 === 0;

const Charge = () => {
  const isMobile = useIsMobile();
  const isElectron = useIsElectron();
  const { requestAxios } = useAxios();
  const [, setModalState] = useRecoilState(modalState);

  const originUrl = getOrigin();

  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [selectedTab, setSelectedTab] = useState<'CHARGE' | 'HISTORY'>(
    'CHARGE'
  );

  useEffect(() => {
    requestPaymentHistory();
  }, []);

  const requestPaymentHistory = async () => {
    try {
      const response = await requestAxios<PaymentHistory[]>(true, {
        url: '/payment/history/',
        method: 'get',
      });

      setPaymentHistory(response);
    } catch (err) {}
  };

  const requestPaymentReady = async (product_id: ChargeType, quantity = 1) => {
    if (product_id === 'hemochat_coin' && !isMultipleOfTen(quantity)) {
      alertMessage('코인은 10개 단위로만 구매 가능합니다.');
      return;
    }

    try {
      const data = await requestAxios<PaymentReadyResponse>(true, {
        url: '/payment/ready/',
        method: 'post',
        data: {
          product_id,
          quantity,
          privacy_term: true,
          agency_term: true,
          check_term: true,
          is_electron: window?.electron ? true : false,
        },
      });

      if (isElectron) {
        window.electron?.openExternal(data.next_redirect_pc_url);
      } else if (isPC()) {
        window.location.href = data.next_redirect_pc_url;
      } else {
        window.location.href = data.next_redirect_mobile_url;
      }
    } catch (error) {
      console.log(error);
      alertMessage('요청처리 중 오류가 발생하였습니다.');
    }
  };

  const onClickHandler = (chargeType: ChargeType) => {
    if (chargeType === ChargeType.COIN) {
      setModalState({
        isOpen: true,
        component: (
          <ChargeModal
            title="코인 개별구매"
            showInput={true}
            defaultValue={CHARGE_UNIT}
            placeholder={`${CHARGE_UNIT}`}
            description={`구매하실 코인 개수를 ${CHARGE_UNIT}개 단위로 입력 후\n결제하기 버튼을 눌러주세요.\n\n구매 가능한 최대 개수는 ${MAX_CHARGABLE_COINS}개입니다.`}
            confirmButtonLabel="결제하기"
            onClickConfirm={(value) => requestPaymentReady(chargeType, value)}
          />
        ),
      });
    } else {
      setModalState({
        isOpen: true,
        component: (
          <ChargeModal
            title={chargeType === ChargeType.BASIC ? 'BASIC 구매' : 'PRO 구매'}
            confirmButtonLabel="결제하기"
            onClickConfirm={() => requestPaymentReady(chargeType)}
          />
        ),
      });
    }
  };

  const onClickInquiry = () => {
    setModalState({
      isOpen: true,
      component: <InquiryModal />,
    });
  };

  const getCoinAmount = (history: PaymentHistory) => {
    if (history.product_name.includes('베이직')) {
      return 110;
    } else if (history.product_name.includes('프로')) {
      return 250;
    } else {
      return history.total_amount / 100;
    }
  };

  // @TODO: api 응답값과 연결하기
  return (
    <Container $isMobile={isMobile}>
      <Navbar type="GOBACK" title="내 코인" />
      {isMobile && (
        <>
          <MobileTab>
            <MobileTabButton
              onClick={() => setSelectedTab('CHARGE')}
              $isSelected={selectedTab === 'CHARGE'}
            >
              코인 충전
            </MobileTabButton>
            <MobileTabButton
              onClick={() => setSelectedTab('HISTORY')}
              $isSelected={selectedTab === 'HISTORY'}
            >
              결제 내역
            </MobileTabButton>
            <MobileTabUnderline
              style={{
                transform:
                  selectedTab === 'CHARGE'
                    ? 'translateX(0%)'
                    : 'translateX(100%)',
              }}
            />
          </MobileTab>
        </>
      )}

      <Wrapper $isMobile={isMobile}>
        {(!isMobile || selectedTab === 'CHARGE') && (
          <>
            {isMobile && (
              <Top $isMobile={isMobile}>
                <p>코인은 1코인당 100원입니다.</p>
                <p>
                  이미지 분석에 사용되는 코인 갯수 : {COINS_FOR_ANALYZE}코인
                  소모
                </p>
                <p>챗봇상담에 사용되는 코인 갯수 : {COINS_FOR_CHAT}코인 소모</p>
              </Top>
            )}
            <ContentWrapper $isMobile={isMobile}>
              {!isMobile && <Title>코인 충전하기</Title>}
              <ItemsWrapper $isMobile={isMobile}>
                <Item $isMobile={isMobile}>
                  <div>
                    <ItemContent>
                      <ItemTitle>코인 개별구매</ItemTitle>
                      <Coin>
                        <CoinIcon />
                        10코인
                      </Coin>
                    </ItemContent>
                    <HR />
                    <ItemContent style={{ justifyContent: 'flex-end' }}>
                      <AfterPrice style={{ marginLeft: '6px' }}>
                        ₩1,000
                      </AfterPrice>
                    </ItemContent>
                  </div>
                  <Button onClick={() => onClickHandler(ChargeType.COIN)}>
                    구매하기
                  </Button>
                </Item>
                <Item $isMobile={isMobile}>
                  <div>
                    <ItemContent>
                      <ItemTitle>Basic</ItemTitle>
                      <Coin>
                        <CoinIcon />
                        110코인
                      </Coin>
                    </ItemContent>
                    <HR />
                    <ItemContent>
                      <Discount>
                        10% <DiscountIcon></DiscountIcon>
                      </Discount>
                      <div>
                        <BeforePrice>11,000</BeforePrice>
                        <AfterPrice style={{ marginLeft: '6px' }}>
                          ₩9,900
                        </AfterPrice>
                      </div>
                    </ItemContent>
                  </div>
                  <Button onClick={() => onClickHandler(ChargeType.BASIC)}>
                    구매하기
                  </Button>
                </Item>
                <Item $isMobile={isMobile}>
                  <div>
                    <ItemContent>
                      <ItemTitle>Pro</ItemTitle>
                      <Coin>
                        <CoinIcon />
                        250코인
                      </Coin>
                    </ItemContent>
                    <HR />
                    <ItemContent>
                      <Discount>
                        20% <DiscountIcon></DiscountIcon>
                      </Discount>
                      <div>
                        <BeforePrice>25,000</BeforePrice>
                        <AfterPrice style={{ marginLeft: '6px' }}>
                          ₩19,900
                        </AfterPrice>
                      </div>
                    </ItemContent>
                  </div>
                  <Button onClick={() => onClickHandler(ChargeType.PRO)}>
                    구매하기
                  </Button>
                </Item>
              </ItemsWrapper>
              {!isMobile && (
                <Top $isMobile={isMobile} style={{ marginTop: '20px' }}>
                  <p>코인은 1코인당 100원입니다.</p>
                  <p>
                    이미지 분석에 사용되는 코인 갯수 : {COINS_FOR_ANALYZE}코인
                    소모
                  </p>
                  <p>
                    챗봇상담에 사용되는 코인 갯수 : {COINS_FOR_CHAT}코인 소모
                  </p>
                </Top>
              )}
            </ContentWrapper>
          </>
        )}
        {(!isMobile || selectedTab === 'HISTORY') && (
          <ContentWrapper $isMobile={isMobile}>
            {!isMobile && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'end',
                }}
              >
                <Title>결제 내역</Title>
                <InquiryButton
                  onClick={onClickInquiry}
                  style={{ marginBottom: '25px', display: 'flex', gap: '3px' }}
                >
                  결제를 했는데 내역이 안보여요!
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: '#9CA5B9',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <InqueryPointer />
                  </div>
                </InquiryButton>
              </div>
            )}

            <HistoryWrapper
              $isMobile={isMobile}
              style={{
                backgroundColor:
                  !isMobile && paymentHistory.length === 0 ? '#323644' : '',
              }}
            >
              {paymentHistory.length === 0 && (
                <EmptyDesc style={{ paddingBottom: isMobile ? '100px' : 0 }}>
                  결제 내역이 없습니다.
                </EmptyDesc>
              )}
              {paymentHistory.map((history, idx) => (
                <HistoryItem key={idx} $isMobile={isMobile}>
                  <HistoryItemTop>
                    <span>{history.product_name} 상품 구매</span>
                    <span>{formatPriceComma(history.total_amount)}원</span>
                  </HistoryItemTop>
                  <HistoryItemBottom>
                    <span>{formatDateString(history.approved_at, false)}</span>
                    <span>{getCoinAmount(history)}코인</span>
                  </HistoryItemBottom>
                </HistoryItem>
              ))}
            </HistoryWrapper>
          </ContentWrapper>
        )}
      </Wrapper>
      {(!isMobile || selectedTab === 'CHARGE') && (
        <Policy>
          서비스 제공기간
          <a target="_blank" href={`${originUrl}/policy/period`}>
            [보기]
          </a>
          {'  '}| 교환 환불규정
          <a target="_blank" href={`${originUrl}/policy/refund`}>
            [보기]
          </a>
          {'  '}
        </Policy>
      )}
      <Footer />
    </Container>
  );
};

export default Charge;

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${({ $isMobile }) => ($isMobile ? '100vh' : '100%')};
  min-height: 100vh;
`;

const Wrapper = styled.div<{ $isMobile: boolean }>`
  max-width: 1140px;
  width: 100%;
  height: 100%;
  display: flex;
  margin: 0 auto;
  position: relative;
  flex-direction: column;
  flex: 1;
`;

const ContentWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  width: 100%;
  max-width: ${({ $isMobile }) => ($isMobile ? '768px' : '750px')};
  flex-direction: column;
  margin: 0 auto;
  line-height: 20px;
  justify-content: ${({ $isMobile }) =>
    $isMobile ? 'space-between' : 'flex-start'};
  height: 100%;
  position: relative;
`;

const Title = styled.h1`
  font-size: 26px;
  color: #fff;
  line-height: 43px;
  margin: 60px 0 20px;
`;

const Top = styled.div<{ $isMobile: boolean }>`
  display: flex;
  background-color: #373944;
  color: #c3cad9;
  font-size: ${({ $isMobile }) => ($isMobile ? '13px' : '14px')};
  height: ${({ $isMobile }) => ($isMobile ? '100px' : '120px')};
  min-height: ${({ $isMobile }) => ($isMobile ? '100px' : '120px')};
  width: 100%;
  padding: 0 20px;
  justify-content: center;
  gap: ${({ $isMobile }) => ($isMobile ? '9px' : '8px')};
  flex-direction: column;
`;

const ItemsWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  padding: ${({ $isMobile }) => ($isMobile ? '20px 20px 30px 20px' : '0')};
`;

const Item = styled.div<{ $isMobile: boolean }>`
  border-radius: 20px;
  background-color: #373944;
  width: ${({ $isMobile }) => ($isMobile ? '100%' : '236px')};
  height: 195px;
  padding: 26px 15px 15px 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ItemTitle = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: #fff;
`;

const ItemContent = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Coin = styled.div`
  display: flex;
  gap: 3px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
`;

const HR = styled.hr`
  height: 1px;
  border: none;
  border-bottom: 1px solid #656d7d;
  margin: 19.5px 0;
`;

const Discount = styled.span`
  color: #ff7474;
  font-size: 14px;
  gap: 2px;
`;

const Button = styled.button`
  border-radius: 10px;
  background-color: #39a2ff;
  width: 100%;
  height: 44px;
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 14px;
`;

const BeforePrice = styled.span`
  text-decoration: line-through;
  font-size: 14px;
  color: #9ca5b9;
`;

const AfterPrice = styled.span`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
`;

const Policy = styled.div`
  width: 100%;
  text-align: center;
  font-size: 14px;
  color: #99a1b5;
  white-space: pre-wrap;
  padding: 20px;
  z-index: 10;

  a {
    text-decoration: underline;
  }
`;

const MobileTab = styled.div`
  width: 100%;
  height: 44px;
  min-height: 44px;
  position: relative;
  border-bottom: 1px solid #3b3f51;
`;

const MobileTabButton = styled.button<{ $isSelected: boolean }>`
  cursor: pointer;
  width: 50%;
  background-color: transparent;
  height: 100%;
  -webkit-tap-highlight-color: transparent;
  color: ${({ $isSelected }) => ($isSelected ? '#39A2FF' : '#9CA5B9')};
  font-weight: 600;
  font-size: 16px;
`;

const MobileTabUnderline = styled.div`
  position: absolute;
  bottom: 1px;
  width: 50vw;
  height: 3px;
  background-color: #39a2ff;
  transition: transform 0.2s ease;
`;

const HistoryWrapper = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  min-height: 200px;
  max-height: ${({ $isMobile }) => ($isMobile ? '100%' : '460px')};
  position: relative;
  border-radius: ${({ $isMobile }) => ($isMobile ? '0' : '12px')};
  overflow-y: auto;
`;

const HistoryItem = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  height: ${({ $isMobile }) => ($isMobile ? '90px' : '87px')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${({ $isMobile }) => ($isMobile ? '0 20px' : '0 35px')};
  background-color: #373944;
  border: solid 1px transparent; /* 기본 테두리 설정 */
  border-top: none;
  border-left: none;
  border-right: none;
  border-image: ${({ $isMobile }) =>
    $isMobile
      ? 'linear-gradient(to right, #656d7d 100%, #656d7d 100%) 1'
      : 'linear-gradient(to right, transparent 3%, #656d7d 3%, #656d7d 97%, transparent 97%) 1'};
  gap: 4px;
  flex-shrink: 0;

  &:last-child {
    border: none;
    border-radius: ${({ $isMobile }) => ($isMobile ? '0' : '0 0 12px 12px')};
  }
`;

const HistoryItemTop = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  font-size: 16px;
  color: #ffffff;
`;

const HistoryItemBottom = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 400;
  font-size: 14px;
  color: #9ca5b9;
`;

const EmptyDesc = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #9ca5b9;
`;

const InquiryButton = styled.button`
  background-color: transparent;
  color: #ffffff;
  text-decoration: underline;
  font-size: 14px;
  text-align: right;
`;
